import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NEVER, Observable, from, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as URI from 'urijs';

import { AuthenticationFailedException } from '®shared/auth/authentication-failed-exception';
import { AppConfig } from '®shared/config';
import { ErrorReportingService } from '®shared/reporting/error-reporting.service';

import { AuthProvider } from './auth-provider';
import { AuthServiceLogin } from './auth-service-login';
import { PersonaPadlockService } from './persona-padlock.service';
import { PersonaPkceSharedService } from './persona-pkce-shared.service';
import { PersonaUser, createPersonaUserFromPkceLogin } from './persona-user';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

@Injectable()
export class PersonaPkceBreakoutAuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  constructor(
    private errorReportingService: ErrorReportingService,
    private personaPadlockService: PersonaPadlockService,
    private personaPkceSharedService: PersonaPkceSharedService,
    router: Router,
    appConfig: AppConfig,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, router, userScopesService);
  }

  private targetUri: URI | null = null;

  public get postBreakoutLoginRedirectUri(): string {
    return this.targetUri.toString();
  }

  public obtainNewToken(returnUrl?: string): Observable<User> {
    const currentUri = returnUrl ? new URI(returnUrl) : this.getCurrentUri();

    const breakoutRoute = '3/breakout-authentication.html';

    this.targetUri = new URI(currentUri.resource());
    return from(
      this.router.navigateByUrl(breakoutRoute, {
        skipLocationChange: true,
      }),
    ).pipe(
      switchMap(() => NEVER),
      catchError(error =>
        this.personaPkceSharedService.throwAuthenticationFailedException(error),
      ),
    );
  }

  public getCurrentToken(): Observable<User> {
    const currentUri = this.getCurrentUri();
    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(currentUri),
    );

    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);
    const bypassFirstPartyCheck = true;

    return this.personaPkceSharedService
      .silentlyGetCurrentToken(
        this.personaPadlockService.silentlyAuthenticate(
          config,
          bypassFirstPartyCheck,
        ),
      )
      .pipe(
        catchError(error =>
          this.personaPkceSharedService.throwAuthenticationFailedException(
            error,
          ),
        ),
      );
  }

  public refreshToken(): Observable<User> {
    return this.personaPkceSharedService.refreshToken(this.currentUser);
  }

  public login(): void {
    const returnUrl = this.getCurrentUri();
    returnUrl.removeQuery('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public loginDuringRouteNavigation(stateUrl: string): void {
    const urlBase = this.getCurrentUri().origin();
    const returnUrl = new URL(stateUrl, urlBase);
    returnUrl.searchParams.delete('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public performBreakoutLogin(): Observable<PersonaUser> {
    if (!this.targetUri) {
      const error = new AuthenticationFailedException(
        'Unable to authenticate: No target URI set, call obtainNewToken first',
      );

      this.errorReportingService.reportToInstana(error);
      return throwError(error);
    }

    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(this.targetUri),
    );

    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    return this.personaPadlockService
      .authenticateViaBreakout(config)
      .pipe(
        map(loginResponse => createPersonaUserFromPkceLogin(loginResponse)),
      );
  }
}
